import ApiService from "@/core/services/api.service";
import axios from "axios";
import querystring from "querystring";
import noPhoto from "@/assets/images/no-photo.png";

const getDefaultState = () => ({
  dashboard_item: {},
  module_list: {},
  leader_board: {},
  registered_trainings: [],
  calendar_items: [],
  collections: [],
  provider_trainings: {},
  assignments: [],
  training_categories: [],
  category_trainings: {},
  internal_instructors: [],
  loading: false,
  error: null,
});

export default {
  namespaced: true,
  state: getDefaultState(),
  mutations: {
    SET_REGISTERED_TRAININGS(state, data) {
      state.registered_trainings = data;
    },
    SET_CALENDAR_ITEMS(state, data) {
      state.calendar_items = data;
    },
    SET_DASHBOARD_ITEM(state, data) {
      state.dashboard_item = data;
    },
    SET_MODULE_LIST(state, data) {
      state.module_list = data;
    },
    SET_LEADER_BOARD(state, data) {
      state.leader_board = data;
    },
    SET_COLLECTIONS(state, data) {
      state.collections = data;
    },
    SET_PROVIDER_TRAININGS(state, { provider, data }) {
      state.provider_trainings = { ...state.provider_trainings, [provider]: data };
    },
    SET_ASSIGNMENTS(state, data) {
      state.assignments = data;
    },
    SET_TRAINING_CATEGORIES(state, data) {
      state.training_categories = data;
    },
    SET_CATEGORY_TRAININGS(state, { key, data }) {
      state.category_trainings = { ...state.category_trainings, [key]: data };
    },
    SET_INTERNAL_INSTRUCTORS(state, data) {
      state.internal_instructors = data;
    },
    SET_LOADING(state, value) {
      state.loading = value;
    },
    SET_ERROR(state, error) {
      state.error = error;
    },
    RESET(state) {
      Object.assign(state, getDefaultState());
    },
  },
  actions: {
    // education-category?tab=can_register_trainings&tabId=0 datasinin Home uyarlamasi
    async fetchRegisteredTrainings({ commit }) {
      commit("SET_LOADING", true);
      try {
        const params = {
          can_show_self_registrable_trainings: true,
          show_registered_trainings: false,
          can_register_trainings: true,
          requests_trainings: true,
          training_kind_id: 0,
          training_type_id: 0,
          order_by: "name",
          by_user: true,
        };
        const response = await ApiService.get(`api/training?${querystring.stringify(params)}`);
        const trainings = response.data?.data?.slice(0, 6) || [];
        commit("SET_REGISTERED_TRAININGS", trainings);
        commit("SET_ERROR", null);
        return { status: true, data: trainings };
      } catch (error) {
        commit("SET_ERROR", error);
        return { status: false, error };
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async fetchCalendarItems({ commit }, { isCatalogVisible }) {
      try {
        let items = [];

        if (isCatalogVisible) {
          const response = await ApiService.get("api/agenda-training?" + querystring.stringify({ by_user: 1, provider: true }));
          if (response.data?.data) {
            items = items.concat(response.data.data.map(item => ({ ...item, _route: 'user.education-detail' })));
          }
        }

        const response = await ApiService.get("api/assignment?" + querystring.stringify({ by_user: 1, provider: true }));
        if (response.data) {
          const assignmentData = Array.isArray(response.data) ? response.data : (response.data.data || []);
          items = items.concat(assignmentData.map(item => ({ ...item, _route: 'user.assignment-detail' })));
        }

        try {
          const noticeResponse = await ApiService.get("api/user/active-notices");
          const noticePayload = noticeResponse.data?.data || noticeResponse.data || [];
          const notices = Array.isArray(noticePayload) ? noticePayload : (noticePayload.data || []);
          items = items.concat(
            notices
              .filter(item => item.start_date && item.end_date)
              .map(item => ({ ...item, _type: 'notice', _route: null }))
          );
        } catch (noticeError) {
          console.error("Active notices load error:", noticeError);
        }

        commit("SET_CALENDAR_ITEMS", items);
        return { status: true, data: items };
      } catch (error) {
        commit("SET_ERROR", error);
        return { status: false, error };
      }
    },

    async fetchCollections({ commit }) {
      try {
        const response = await ApiService.get("api/collection-all?" + querystring.stringify({ order_by: "created_at" }));
        const collections = Array.isArray(response.data) ? response.data : (response.data?.data || []);
        commit("SET_COLLECTIONS", collections);
        return { status: true, data: collections };
      } catch (error) {
        commit("SET_ERROR", error);
        return { status: false, error };
      }
    },

    async fetchDashboardData({ commit }) {
      try {
        const response = await ApiService.get("api/dashboard");
        commit("SET_DASHBOARD_ITEM", response.data);
        return { status: true, data: response.data };
      } catch (error) {
        commit("SET_ERROR", error);
        return { status: false, error };
      }
    },

    async fetchModuleList({ commit }, companyId) {
      try {
        const response = await axios.get(`${process.env.VUE_APP_BACKEND_APP_URL}/api/widgets`, {
          params: { company_id: companyId, by_user: true },
        });
        commit("SET_MODULE_LIST", response.data);
        return { status: true, data: response.data };
      } catch (error) {
        commit("SET_ERROR", error);
        return { status: false, error };
      }
    },

    async fetchLeaderBoard({ commit }) {
      try {
        const response = await ApiService.get("api/dashboard/leader-board");
        commit("SET_LEADER_BOARD", response.data);
        return { status: true, data: response.data };
      } catch (error) {
        commit("SET_ERROR", error);
        return { status: false, error };
      }
    },

    async fetchProviderTrainings({ commit }, { provider, limit = 6 }) {
      try {
        const params = { limit, provider };
        const response = await ApiService.get(`api/provider-search?${querystring.stringify(params)}`);
        const data = response.data?.data || response.data || [];
        commit("SET_PROVIDER_TRAININGS", { provider, data });
        return { status: true, data };
      } catch (error) {
        commit("SET_ERROR", error);
        return { status: false, error };
      }
    },

    async fetchAssignments({ commit }) {
      try {
        const response = await ApiService.get("api/assignment?" + querystring.stringify({ by_user: 1, provider: true }));
        const data = Array.isArray(response.data) ? response.data : (response.data?.data || []);
        commit("SET_ASSIGNMENTS", data);
        return { status: true, data };
      } catch (error) {
        commit("SET_ERROR", error);
        return { status: false, error };
      }
    },

    async fetchHomeAssignments({ commit }) {
      try {
        const params = {
          just_assigned: false,
          on_progress: false,
          repeat_it: false,
          search: '',
          order_by: '',
          order: 'desc',
          with_expired: false,
          perPage: 6,
          by_user: true,
          completed: false,
          sort: 'desc',
          page: 1,
        };
        const response = await ApiService.get("api/assignment?" + querystring.stringify(params));
        const data = response.data?.data || [];
        commit("SET_ASSIGNMENTS", data);
        return { status: true, data };
      } catch (error) {
        commit("SET_ERROR", error);
        return { status: false, error };
      }
    },

    async fetchTrainingCategories({ commit }) {
      try {
        const response = await ApiService.get("api/training-type");
        const all = response.data?.data || response.data || [];
        const categories = all.filter(item => item.company_id !== 1);
        commit("SET_TRAINING_CATEGORIES", categories);
        return { status: true, data: categories };
      } catch (error) {
        commit("SET_ERROR", error);
        return { status: false, error };
      }
    },

    async fetchCategoryTrainings({ commit }, { categoryId, tab }) {
      try {
        const isAll = !categoryId || categoryId === 0;
        const params = {
          page: 1,
          search: "",
          order_by: "name",
          by_user: true,
          can_show_self_registrable_trainings: true,
          show_registered_trainings: tab === 'registered',
          can_register_trainings: tab === 'can_register',
          requests_trainings: tab === 'can_register',
          training_kind_id: 0,
        };
        if (isAll) {
          params.training_type_id = 0;
        } else {
          params["training_type_id[]"] = categoryId;
        }
        const response = await ApiService.get(`api/training?${querystring.stringify(params)}`);
        const trainings = response.data?.data?.slice(0, 6) || [];
        const key = `${categoryId}_${tab}`;
        commit("SET_CATEGORY_TRAININGS", { key, data: trainings });
        return { status: true, data: trainings };
      } catch (error) {
        commit("SET_ERROR", error);
        return { status: false, error };
      }
    },

    async fetchInternalInstructors({ commit }) {
      try {
        const response = await ApiService.get(
          `api/my-instructors?${querystring.stringify({ type: "internal" })}`
        );
        const payload = response.data;
        const instructorItems = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.data)
            ? payload.data
            : Array.isArray(payload?.data?.data)
              ? payload.data.data
              : [];

        const instructors = instructorItems
          .map((item) => {
            const instructor = item.instructor || item;
            const user = instructor.user || item;
            const trainings = Array.isArray(item.trainings)
              ? item.trainings
              : Array.isArray(instructor.trainings)
                ? instructor.trainings
                : [];
            const relationTitle = user.title;
            const title = (
              typeof relationTitle === "string"
                ? relationTitle
                : relationTitle?.name || relationTitle?.title
            ) || user.title_name || user.position_name || "";

            return {
              id: instructor.instructor_id || instructor.id,
              userId: user.id,
              name:
                [user.name, user.surname].filter(Boolean).join(" ").trim()
                || user.full_name
                || "",
              photo:
                user.photo_link
                || user.avatar
                || user.photo
                || instructor.avatar
                || noPhoto,
              title,
              department:
                typeof user.department === "string"
                  ? user.department
                  : user.department?.name || user.department_name || "",
              bio: instructor.bio || "",
              expertiseAreas: Array.isArray(instructor.expertise_areas)
                ? instructor.expertise_areas
                : [],
              trainings,
              trainingCount:
                instructor.training_count
                ?? instructor.trainings_count
                ?? null,
            };
          })
          .filter((instructor) => Number.isFinite(Number(instructor.id)));

        commit("SET_INTERNAL_INSTRUCTORS", instructors);
        return { status: true, data: instructors };
      } catch (error) {
        commit("SET_INTERNAL_INSTRUCTORS", []);
        commit("SET_ERROR", error);
        return { status: false, error };
      }
    },

    resetDashboardAzcon({ commit }) {
      commit("RESET");
    },
  },
  getters: {
    dashboardItem: state => state.dashboard_item,
    moduleList: state => state.module_list,
    leaderBoard: state => state.leader_board,
    registeredTrainings: state => state.registered_trainings,
    calendarItems: state => state.calendar_items,
    collections: state => state.collections,
    providerTrainings: state => state.provider_trainings,
    assignments: state => state.assignments,
    trainingCategories: state => state.training_categories,
    categoryTrainings: state => state.category_trainings,
    internalInstructors: state => state.internal_instructors,
    loading: state => state.loading,
    error: state => state.error,
    hasData: state => {
      return Object.keys(state.dashboard_item).length > 0 ||
        state.registered_trainings.length > 0 ||
        state.calendar_items.length > 0 ||
        state.collections.length > 0 ||
        state.internal_instructors.length > 0;
    },
  },
};
